import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Database, Sparkles, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import {
  useEnsureLearnSchemaMutation,
  useSeedLearnCatalogMutation,
} from '@/client/api/learn';

interface BootstrapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
}

export const BootstrapDialog: React.FC<BootstrapDialogProps> = ({
  open,
  onOpenChange,
  onCompleted,
}) => {
  const ensureSchemaMutation = useEnsureLearnSchemaMutation();
  const seedCatalogMutation = useSeedLearnCatalogMutation();

  const [publishOnSeed, setPublishOnSeed] = useState(false);
  const [schemaEnsured, setSchemaEnsured] = useState(false);
  const [seedResult, setSeedResult] = useState<{
    topics?: number;
    sections?: number;
    skipped?: number;
  } | null>(null);

  const handleEnsureSchema = async () => {
    try {
      const res = await ensureSchemaMutation.mutateAsync();
      setSchemaEnsured(true);
      toast.success('Schema ensured', {
        description: `Content types created: ${res.content_types_created ?? 0}, Fields created: ${res.fields_created ?? 0}`,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to ensure schema');
    }
  };

  const handleSeedCatalog = async () => {
    try {
      const res = await seedCatalogMutation.mutateAsync(publishOnSeed);
      setSeedResult({
        topics: res.topics_created,
        sections: res.sections_created,
        skipped: res.skipped,
      });
      toast.success('Learn catalog seeded', {
        description: `Created ${res.topics_created ?? 0} topics and ${res.sections_created ?? 0} sections (${res.skipped ?? 0} skipped).`,
      });
      onCompleted?.();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to seed catalog');
    }
  };

  const handleFullBootstrap = async () => {
    try {
      const schemaRes = await ensureSchemaMutation.mutateAsync();
      setSchemaEnsured(true);
      const seedRes = await seedCatalogMutation.mutateAsync(publishOnSeed);
      setSeedResult({
        topics: seedRes.topics_created,
        sections: seedRes.sections_created,
        skipped: seedRes.skipped,
      });
      toast.success('Learn catalog successfully bootstrapped', {
        description: `Schema ready. Seeded ${seedRes.topics_created ?? 0} topics, ${seedRes.sections_created ?? 0} sections.`,
      });
      onCompleted?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || 'Bootstrap failed');
    }
  };

  const isPending = ensureSchemaMutation.isPending || seedCatalogMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Learn Schema & Seed Bootstrap
          </DialogTitle>
          <DialogDescription>
            Learn uses runtime CMS content types (`learn_topic`, `learn_section`) and custom field definitions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Step 1: Ensure Schema */}
          <div className="rounded-lg border border-gray-200 p-3.5 space-y-2 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-900">1. Ensure Schema</span>
              </div>
              {schemaEnsured && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Ensured
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Creates `learn_topic` and `learn_section` CMS content types and the 7 required field definitions. Idempotent.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={handleEnsureSchema}
              disabled={isPending || schemaEnsured}
            >
              {ensureSchemaMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Ensuring...
                </>
              ) : (
                'Run Ensure Schema'
              )}
            </Button>
          </div>

          {/* Step 2: Seed Catalog */}
          <div className="rounded-lg border border-gray-200 p-3.5 space-y-3 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-900">2. Seed v1 Catalog</span>
              </div>
              {seedResult && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Seeded
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Loads 8 default topics and 20 sections exported from the Learn specification. Existing slugs are skipped.
            </p>

            <div className="flex items-center justify-between pt-1">
              <div className="space-y-0.5">
                <Label htmlFor="seed-publish-toggle" className="text-xs font-medium">
                  Publish upon seed
                </Label>
                <p className="text-[11px] text-gray-400">
                  Default is draft mode so you can review first.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  id="seed-publish-toggle"
                  type="checkbox"
                  checked={publishOnSeed}
                  onChange={(e) => setPublishOnSeed(e.target.checked)}
                  disabled={isPending}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={handleSeedCatalog}
              disabled={isPending}
            >
              {seedCatalogMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Seeding...
                </>
              ) : (
                'Seed Catalog'
              )}
            </Button>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
            <AlertTriangle className="h-4 w-4 shrink-0 text-blue-600 mt-0.5" />
            <span>
              You can run 1-Click Bootstrap to ensure schema and seed the catalog in one go.
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={handleFullBootstrap}
            disabled={isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Bootstrapping...
              </>
            ) : (
              '1-Click Bootstrap'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
